import { Component, Element, h, Host, Prop } from "@stencil/core";
import pfx from "../../../utils/style-class-prefix-helper";
import { isMobile } from "../../../utils/screen-helper";
import {
  LocalizableComponent,
  Localization,
  setCurrentLocalizationDelegate
} from "../../../common/localizable-component";
import { LinkLocalization } from "../constants/contact-bar-types";
import { LINK_LOCALIZATION } from "../constants/contact-bar-i18n";
import { stringFormat } from "../../../utils/string-format";
import Fragment from "../../../common/fragment";
import { getFirstNestedChild, getNestedChildrenOf } from "../../../utils/dom-helper";

@Component({
  tag: "eon-ui-contact-bar-item",
  styleUrl: "contact-bar-item.scss",
  shadow: true
})
export class ContactBarItem implements LocalizableComponent<LinkLocalization> {
  @Element() hostElement: HTMLEonUiContactBarItemElement;

  localization: Localization<LinkLocalization> = LINK_LOCALIZATION;
  currentLocalization: LinkLocalization;
  private slottedContactLayer: HTMLEonUiContactLayerElement;
  private slottedContactLayers: Array<HTMLEonUiContactLayerElement>;

  /**
   * Type of contact bar item
   */
  @Prop() itemType: "mail" | "phone" | "layer" | "link";

  /**
   * Provide an e-mail without mailto: for "mail" type, phone as a numer for "phone" type, a normal link for other types
   */
  @Prop() link: string;

  /**
   * Icon name
   */
  @Prop() icon: string;

  /**
   * Text to display
   */
  @Prop() text: string;

  /**
   * Aria-Label for accessibility
   */
  @Prop() ariaLabel: string;

  linkPrefix: string = "";
  linkTarget: "_blank" | "_self" | "_parent" | "_top";
  linkRole: string = "";
  onClick: any;
  onKeyPress: any;

  componentWillLoad() {
    this.slottedContactLayer = getFirstNestedChild(
      this.hostElement,
      "eon-ui-contact-layer"
    ) as HTMLEonUiContactLayerElement;
    this.slottedContactLayers = Array.from(getNestedChildrenOf(this.hostElement.parentElement, "eon-ui-contact-layer"));
    if (this.slottedContactLayer) this.slottedContactLayer.visible = false;
    this.setCurrentLocalization();
    this.assignVariables();
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  onClickHandlerForContactLayer(event) {
    event.preventDefault();

    window.setTimeout(() => {
      if (this.slottedContactLayer.visible) {
        this.slottedContactLayer.visible = false;
      } else {
        this.slottedContactLayers.forEach((contactLayer) => {
          contactLayer.visible = false;
        });
        this.slottedContactLayer.visible = true;
      }
    }, 0);
  }

  assignVariables() {
    switch (this.itemType) {
      case "mail":
        this.linkPrefix = "mailto:";
        this.linkTarget = "_blank";
        this.ariaLabel = stringFormat(this.currentLocalization.ariaLabel.mail, this.text);
        break;
      case "phone":
        this.linkPrefix = "tel:";
        this.linkTarget = "_self";
        this.ariaLabel = stringFormat(this.currentLocalization.ariaLabel.phone, this.text);
        break;
      case "layer":
        this.linkTarget = "_self";
        this.onClick = (event) => this.onClickHandlerForContactLayer(event);
        break;
      case "link":
        this.linkTarget = "_self";
        this.ariaLabel = stringFormat(this.currentLocalization.ariaLabel.faq, this.text);
        break;
      default:
        this.linkTarget = "_blank";
    }
  }

  render() {
    return (
      <Host>
        <div class={pfx(`contact-bar-item__${isMobile() ? "modal-" : ""}item`)}>
          <div
            class={pfx(`contact-bar-item__${isMobile() ? "modal-" : ""}content`)}
            role={this.linkRole}
            onClick={this.onClick}
            onKeyPress={this.onKeyPress}
          >
            <span class={pfx({ [`contact-bar-item__${isMobile() ? "modal-" : ""}content-icon`]: true })}>
              <eon-ui-button rank="primary" size="small" icon={this.icon}></eon-ui-button>
            </span>
            <span class={pfx(`contact-bar-item__${isMobile() ? "modal-" : ""}content-link`)}>
              <slot name="live-chat"></slot>
              <eon-ui-link
                target={this.linkTarget}
                text={this.text}
                hide-icon={true}
                ariaLabel={this.ariaLabel}
                href={this.linkPrefix + this.link}
              ></eon-ui-link>
            </span>
          </div>
        </div>
        <Fragment>
          <slot name="contact-layer"></slot>
        </Fragment>
      </Host>
    );
  }
}
